/**
 * Conversion entre le modèle de reçu utilisé par les écrans et la charge utile
 * attendue par l'API.
 *
 * Même contrat que `certificateApi.js` : la couleur part sous forme
 * hexadécimale (le serveur ne connaît pas nos identifiants de palette) et les
 * booléens sous forme 0/1.
 *
 * Le contenu rédactionnel — titre, mention de bas de page, préfixe de
 * numérotation — ne circule pas : il suit la langue de l'utilisateur et est
 * résolu à l'émission (voir receiptModel).
 */
import {
  RECEIPT_FORMATS,
  RECEIPT_PALETTES,
  RECEIPT_TEMPLATES,
  defaultReceipt,
  paletteOf,
} from "@/utils/receiptModel";

export function toApiPayload(receipt) {
  const model = receipt || {};
  return {
    template: model.template,
    format: model.format,
    color: paletteOf(model.palette).accent,
    signature: {
      role: model.signatory_role || "",
      name: model.signatory_name || "",
    },
    show_signature: model.show_signature ? 1 : 0,
    show_logo: model.show_logo ? 1 : 0,
    show_school_contacts: model.show_school_contacts ? 1 : 0,
  };
}

/** Retrouve la palette à partir de sa couleur d'accent. */
function paletteFromColor(color) {
  if (!color) return null;
  const value = String(color).trim().toLowerCase();
  const match = RECEIPT_PALETTES.find((p) => p.accent.toLowerCase() === value);
  return match ? match.id : null;
}

/** Booléen exprimé en 0/1 côté API, tolérant aux vrais booléens. */
function bool(value, fallback) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  return Number(value) === 1;
}

export function fromApiPayload(data) {
  const base = defaultReceipt();
  if (!data || typeof data !== "object") return base;

  const signature = data.signature || {};

  return {
    ...base,
    template: RECEIPT_TEMPLATES.includes(data.template) ? data.template : base.template,
    format: RECEIPT_FORMATS.includes(data.format) ? data.format : base.format,
    palette: paletteFromColor(data.color) || base.palette,
    signatory_role: signature.role || base.signatory_role,
    signatory_name: signature.name || "",
    show_signature: bool(data.show_signature, base.show_signature),
    show_logo: bool(data.show_logo, base.show_logo),
    show_school_contacts: bool(data.show_school_contacts, base.show_school_contacts),
  };
}

/** Identifiant de la ressource, quel que soit le nom du champ renvoyé. */
export function extractConfigId(data) {
  if (!data || typeof data !== "object") return null;
  return data.id || data._id || data.pk || null;
}
